if (!customElements.get('studio-reviews')) {
  customElements.define('studio-reviews', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.sr-track');
      this.cards = Array.from(this.querySelectorAll('.sr-card'));
      if (!this.track || !this.cards.length) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.counter = this.querySelector('.sr-counter');
      this.pauseButton = this.querySelector('.sr-pause');
      this.arrows = Array.from(this.querySelectorAll('.sr-arrow'));
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.inView = false;
      this.editor = Boolean(window.Shopify?.designMode);
      this.arrows.forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => { this.pause(); this.move(Number(button.dataset.direction)); }, options);
      });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('pointerdown', () => this.pause(), options);
      this.track.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.pause();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); }
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => this.schedule(), options);
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.schedule();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(item => item.dataset.reviewBlock === event.detail.blockId);
        if (!card) return;
        this.pause();
        this.goTo(card, 'instant');
      }, options);
      this.resize = new ResizeObserver(() => { this.update(); this.schedule(); });
      this.resize.observe(this.track);
      this.visibility = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.15 });
      this.visibility.observe(this);
      this.update();
      this.schedule();
    }

    get overflows() { return this.track.scrollWidth > this.track.clientWidth + 2; }

    update() {
      const frame = this.track.getBoundingClientRect();
      const visible = this.cards.map((card, index) => ({ index, rect: card.getBoundingClientRect() }))
        .filter(({ rect }) => Math.min(rect.right, frame.right) - Math.max(rect.left, frame.left) > rect.width * 0.6);
      this.firstVisible = visible[0]?.index || 0;
      const first = this.firstVisible + 1;
      const last = (visible[visible.length - 1]?.index ?? this.firstVisible) + 1;
      const label = first === last ? `${first} / ${this.cards.length}` : `${first}–${last} / ${this.cards.length}`;
      if (this.counter.textContent !== label) this.counter.textContent = label;
      this.arrows.forEach(button => { button.disabled = !this.overflows; });
      this.counter.hidden = !this.overflows;
    }

    goTo(card, behavior) {
      const frame = this.track.getBoundingClientRect();
      const rect = card.getBoundingClientRect();
      const centered = getComputedStyle(card).scrollSnapAlign === 'center';
      const offset = centered ? (frame.width - rect.width) / 2 : parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      this.track.scrollTo({ left: this.track.scrollLeft + rect.left - frame.left - offset, behavior: behavior || (this.motion.matches ? 'instant' : 'smooth') });
    }

    move(direction) {
      if (!this.overflows) return;
      const end = this.track.scrollWidth - this.track.clientWidth;
      const atStart = this.track.scrollLeft < 3;
      const atEnd = this.track.scrollLeft > end - 3;
      let index = Math.max(0, Math.min(this.cards.length - 1, this.firstVisible + direction));
      if (direction > 0 && atEnd) index = 0;
      if (direction < 0 && atStart) index = this.cards.length - 1;
      this.goTo(this.cards[index]);
    }

    pause() { this.userPaused = true; this.schedule(); }

    schedule() {
      clearTimeout(this.timer);
      const autoplay = this.dataset.autoplay === 'true' && !this.editor && !this.motion.matches && this.overflows;
      this.counter.setAttribute('aria-live', autoplay && !this.userPaused ? 'off' : 'polite');
      if (this.pauseButton) {
        this.pauseButton.hidden = !autoplay;
        this.pauseButton.textContent = this.userPaused ? 'Afspelen' : 'Pauzeren';
        this.pauseButton.setAttribute('aria-label', this.userPaused ? 'Automatisch schuiven hervatten' : 'Automatisch schuiven pauzeren');
      }
      if (!autoplay || this.userPaused || this.hovered || this.focused || !this.inView || document.hidden) return;
      this.timer = setTimeout(() => { this.move(1); this.schedule(); }, Number(this.dataset.interval) || 5000);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.scrollFrame);
    }
  });
}
